import { useState, type FormEvent } from "react";
import type { OrderDetail, UpdateReceiptInput } from "./orderingApi";
import { priceToCents } from "./orderingFormUtils";

export function ReceiptCreateForm({
  lines: orderLines,
  isSaving,
  onSubmit,
}: {
  lines: OrderDetail["lines"];
  isSaving: boolean;
  onSubmit: (lines: UpdateReceiptInput["lines"]) => void;
}) {
  const [selectedLineIds, setSelectedLineIds] = useState(
    () => new Set<string>(),
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  const remainingLines = orderLines.filter(
    (line) => line.receivedQuantity < line.orderedQuantity,
  );
  const submitReceipt = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSaving) return;
    setValidationError(null);
    const formData = new FormData(event.currentTarget);
    const lines: UpdateReceiptInput["lines"] = [];
    for (const line of remainingLines) {
      if (!selectedLineIds.has(line.id)) continue;
      const receivedQuantity = Number(formData.get(`quantity-${line.id}`));
      const paidUnitPriceCents = priceToCents(formData.get(`price-${line.id}`));
      if (
        !Number.isInteger(receivedQuantity) ||
        receivedQuantity <= 0 ||
        receivedQuantity > line.orderedQuantity - line.receivedQuantity ||
        paidUnitPriceCents === null
      ) {
        setValidationError(
          "Vérifiez les quantités et les prix des lignes cochées.",
        );
        return;
      }
      lines.push({
        orderLineId: line.id,
        receivedQuantity,
        paidUnitPriceCents,
      });
    }
    if (lines.length === 0) {
      setValidationError("Cochez au moins une ligne reçue.");
      return;
    }
    onSubmit(lines);
  };
  return (
    <form
      className="space-y-3 border-t border-slate-200 pt-4"
      aria-label="Enregistrer une réception"
      onSubmit={submitReceipt}
    >
      <div>
        <h3 className="font-semibold">Enregistrer une réception</h3>
        <p className="text-sm text-slate-500">
          Cochez les articles reçus. Les quantités restantes et les prix sont
          préremplis ; vous pouvez les ajuster avant d’enregistrer.
        </p>
      </div>
      <fieldset disabled={isSaving} className="space-y-3 disabled:opacity-60">
        {remainingLines.map((line) => {
          const remaining = line.orderedQuantity - line.receivedQuantity;
          const isSelected = selectedLineIds.has(line.id);
          return (
            <div
              key={line.id}
              className="grid gap-2 rounded-lg bg-slate-50 p-3 sm:grid-cols-[minmax(0,1fr)_100px_130px]"
            >
              <label className="flex cursor-pointer items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  className="mt-1 size-4 shrink-0"
                  checked={isSelected}
                  aria-label={`Article reçu : ${line.commercialName}`}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setSelectedLineIds((current) => {
                      const selected = new Set(current);
                      if (checked) selected.add(line.id);
                      else selected.delete(line.id);
                      return selected;
                    });
                    setValidationError(null);
                  }}
                />
                <span>
                  <span className="block font-medium">
                    {[line.brand, line.commercialName]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                  <span className="text-xs text-slate-500">
                    Reste à recevoir : {remaining}
                  </span>
                </span>
              </label>
              <label className="text-xs text-slate-500">
                Quantité reçue
                <input
                  name={`quantity-${line.id}`}
                  type="number"
                  min="1"
                  max={remaining}
                  step="1"
                  required
                  disabled={!isSelected}
                  defaultValue={remaining}
                  className="block w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 disabled:opacity-50"
                  aria-label={`Quantité reçue pour ${line.commercialName}`}
                />
              </label>
              <label className="text-xs text-slate-500">
                Prix unitaire payé (€)
                <input
                  name={`price-${line.id}`}
                  inputMode="decimal"
                  required
                  disabled={!isSelected}
                  defaultValue={(line.catalogUnitPriceCents / 100).toFixed(2)}
                  className="block w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 disabled:opacity-50"
                  aria-label={`Prix payé pour ${line.commercialName}`}
                />
              </label>
            </div>
          );
        })}
        <button
          type="submit"
          disabled={selectedLineIds.size === 0}
          className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
        >
          {isSaving ? "Enregistrement..." : "Enregistrer la réception"}
        </button>
      </fieldset>
      {validationError ? (
        <p role="alert" className="text-sm text-red-700">
          {validationError}
        </p>
      ) : null}
    </form>
  );
}
